import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface AccordionItem {
  id: string;
  title: React.ReactNode;
  /** Optional right-aligned hint shown on the header (e.g. a price). */
  meta?: React.ReactNode;
  content: React.ReactNode;
}

interface Props {
  items: AccordionItem[];
  /** Item ids open on first render. */
  defaultOpen?: string[];
  /** Allow several panels open at once (default: one at a time). */
  multiple?: boolean;
  /** Heading level of the item headers (2 when directly under the page h1, 3 under an h2). */
  level?: 2 | 3;
  className?: string;
}

/** WAI-ARIA accordion: header buttons with aria-expanded/controls, panels as labelled regions. */
const Accordion: React.FC<Props> = ({ items, defaultOpen = [], multiple = false, level = 3, className = '' }) => {
  const Heading = `h${level}` as 'h2' | 'h3';
  const base = useId();
  const [open, setOpen] = useState<string[]>(defaultOpen);

  const toggle = (id: string) =>
    setOpen((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : multiple ? [...cur, id] : [id]));

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {items.map((item) => {
        const isOpen = open.includes(item.id);
        const btnId = `${base}-h-${item.id}`;
        const panelId = `${base}-p-${item.id}`;
        return (
          <div
            key={item.id}
            className="rounded-2xl border border-secondary-800 bg-secondary-900/50 transition-[box-shadow,border-color,background-color] duration-200 hover:border-primary-400/35 hover:bg-secondary-800/35 hover:shadow-[0_0_18px_rgba(14,165,233,0.16)]"
          >
            <Heading className="m-0">
              <button
                type="button"
                id={btnId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(item.id)}
                className="w-full min-h-[48px] px-4 sm:px-5 py-3 flex items-center justify-between gap-3 text-left text-sm sm:text-base font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400"
              >
                <span className="min-w-0">{item.title}</span>
                <span className="flex items-center gap-3 shrink-0">
                  {item.meta && <span className="text-sm font-medium text-secondary-300">{item.meta}</span>}
                  <ChevronDown
                    className={`w-5 h-5 text-secondary-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </button>
            </Heading>
            <div
              id={panelId}
              role="region"
              aria-labelledby={btnId}
              hidden={!isOpen}
              className="px-4 sm:px-5 pt-3 sm:pt-4 pb-4 sm:pb-5 text-sm text-secondary-300 leading-relaxed"
            >
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
